import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom'; // Import hooks
import apiService from '../../services/api';
import TranscriptDisplay from './TranscriptDisplay';
import SimulationExportModal from './SimulationExportModal';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  Typography,
  Box,
  CircularProgress,
  Card,
  CardContent,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Chip,
  Grid,
  Divider
} from '@mui/material';
import {
  Download as DownloadIcon,
  ExpandMore as ExpandMoreIcon,
  Person as PersonIcon,
  Psychology as PsychologyIcon,
  Settings as SettingsIcon,
  Timer as TimerIcon,
  Message as MessageIcon
} from '@mui/icons-material';

const SimulationHistoryViewer = ({ token, simulationIdFromUrl }) => {
  const navigate = useNavigate();
  const location = useLocation();

  const [simulations, setSimulations] = useState([]);
  const [selectedSimulation, setSelectedSimulation] = useState(null);
  const [lastViewedSimId, setLastViewedSimId] = useState(null);
  const [isLoadingList, setIsLoadingList] = useState(false);
  const [isLoadingDetails, setIsLoadingDetails] = useState(false);
  const [error, setError] = useState('');

  // State for review and summary
  const [review, setReview] = useState(null);
  const [summary, setSummary] = useState(null);
  const [isReviewLoading, setIsReviewLoading] = useState(false);
  const [isSummaryLoading, setIsSummaryLoading] = useState(false);
  const [reviewError, setReviewError] = useState('');
  const [summaryError, setSummaryError] = useState('');
  
  // State for export modal
  const [exportModalOpen, setExportModalOpen] = useState(false);

  const fromRunner = location.state?.fromRunner;
  const directLoadSimulationId = simulationIdFromUrl;

  const fetchSimulationsList = async () => {
    if (!token) return;
    setIsLoadingList(true);
    setError('');
    setReview(null); // Clear previous review/summary when loading new simulation
    setSummary(null);
    setReviewError('');
    setSummaryError('');
    try {
      const data = await apiService.getSimulationsList(token);
      setSimulations(data || []);
    } catch (err) {
      setError('Failed to load simulation history.');
      console.error(err);
    } finally {
      setIsLoadingList(false);
    }
  };

  useEffect(() => {
    if (directLoadSimulationId) {
      handleSelectSimulation(directLoadSimulationId);
    } else {
      // If not loading a specific sim, and no sim selected, fetch list
      if (!selectedSimulation) {
        fetchSimulationsList();
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, directLoadSimulationId]);


  const handleSelectSimulation = async (simulationId) => {
    if (!token || !simulationId) return;
    if (selectedSimulation && selectedSimulation._id === simulationId && !location.state?.fromRunner && !directLoadSimulationId) { // Avoid re-fetch if same and not a direct load/from runner
        return;
    }

    setIsLoadingDetails(true);
    setError('');
    setReview(null);
    setSummary(null);
    setReviewError('');
    setSummaryError('');

    try {
      const data = await apiService.getSimulationDetails(simulationId, token);
      setSelectedSimulation(data);
      setLastViewedSimId(simulationId);
      if (data.review) setReview(data.review);
      if (data.summary) setSummary(data.summary);
    } catch (err) {
      setError(`Failed to load details for simulation ${simulationId}.`);
      setSelectedSimulation(null);
      console.error(err);
    } finally {
      setIsLoadingDetails(false);
    }
  };
  
  const handleRequestReview = async () => {
    if (!selectedSimulation || !token) return;
    setIsReviewLoading(true);
    setReviewError('');
    try {
      const updatedSim = await apiService.requestSimulationReview(selectedSimulation._id, token);
      setReview(updatedSim.review);
      setSelectedSimulation(prev => ({...prev, review: updatedSim.review})); // Update selected sim too
    } catch (err) {
      setReviewError(`Failed to request review: ${err.message}`);
      console.error(err);
    } finally {
      setIsReviewLoading(false);
    }
  };

  const handleGenerateSummary = async () => {
    if (!selectedSimulation || !token) return;
    setIsSummaryLoading(true);
    setSummaryError('');
    try {
      const updatedSim = await apiService.generateSimulationSummary(selectedSimulation._id, token);
      setSummary(updatedSim.summary);
      setSelectedSimulation(prev => ({...prev, summary: updatedSim.summary})); // Update selected sim too
    } catch (err) {
      setSummaryError(`Failed to generate summary: ${err.message}`);
      console.error(err);
    } finally {
      setIsSummaryLoading(false);
    }
  };

  const handleBackToList = () => {
    setSelectedSimulation(null);
    setReview(null);
    setSummary(null);
    if (directLoadSimulationId && simulations.length === 0 && !isLoadingList) {
        fetchSimulationsList(); 
    }
    if (directLoadSimulationId || location.pathname.includes('/history/')) { // More robust check for being in detail view via URL
      navigate('/admin/dashboard/history', { replace: true }); // Navigate to the list view path
    }
  };

  const handleRunAnotherSimulation = () => {
    navigate('/admin/dashboard/run-simulation'); 
  };
  
  const handleExportSimulation = () => {
    if (selectedSimulation) {
      setExportModalOpen(true);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleString();
  };

  // Determine which button to show in detail view - removed sx={{ mb: 2 }} as it's now in the sticky header
  const DetailViewNavigationButton = () => {
    if (fromRunner || (directLoadSimulationId && location.state?.fromRunner)) { // Check both direct load with state and stateful navigation
      return (
        <Button onClick={handleRunAnotherSimulation} variant="outlined">
          Run Another Simulation
        </Button>
      );
    }
    return (
      <Button onClick={handleBackToList} variant="outlined">
        &larr; Back to List
      </Button>
    );
  };

  return (
    <Box sx={{ p: 2 }}>
      <Typography variant="h5" gutterBottom>Simulation History</Typography>

      {error && <Typography color="error" sx={{ mb: 2 }}>{error}</Typography>}

      {selectedSimulation ? (
        // Detail View
        <>
          {/* Sticky Action Bar */}
          <Paper 
            elevation={2} 
            sx={{ 
              position: 'sticky',
              top: 0,
              zIndex: 100,
              p: 2,
              mb: 2,
              backgroundColor: 'background.paper',
              borderRadius: 1
            }}
          >
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <DetailViewNavigationButton />
              </Box>
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                <Button 
                  onClick={handleRequestReview} 
                  disabled={isReviewLoading || !!review?.evaluation}
                  variant={review?.evaluation ? "outlined" : "contained"}
                  size="medium"
                  color={review?.evaluation ? "success" : "primary"}
                >
                  {isReviewLoading ? 'Generating Review...' : (review?.evaluation ? '✓ Review Generated' : 'Generate Review')}
                </Button>
                <Button 
                  onClick={handleGenerateSummary} 
                  disabled={isSummaryLoading}
                  variant={(summary?.therapistView && summary?.clientView && 
                           !summary.therapistView.includes("Unable to generate") && 
                           !summary.clientView.includes("Unable to generate")) ? "outlined" : "contained"}
                  size="medium"
                  color={(summary?.therapistView && summary?.clientView && 
                         !summary.therapistView.includes("Unable to generate") && 
                         !summary.clientView.includes("Unable to generate")) ? "success" : 
                         ((summary?.therapistView || summary?.clientView) ? "warning" : "primary")}
                >
                  {isSummaryLoading ? 'Generating Summaries...' : 
                   ((summary?.therapistView && summary?.clientView && 
                     !summary.therapistView.includes("Unable to generate") && 
                     !summary.clientView.includes("Unable to generate")) ? 'Regenerate Summaries' : 
                    ((summary?.therapistView || summary?.clientView) ? 'Retry Summary Generation' : 'Generate Summaries'))}
                </Button>
                <Button
                  onClick={handleExportSimulation}
                  variant="outlined"
                  size="medium"
                  startIcon={<DownloadIcon />}
                >
                  Export
                </Button>
              </Box>
            </Box>
          </Paper>

          <Paper elevation={3} sx={{ p: 2 }}>
            <Typography variant="h6" sx={{ mb: 2 }}>Simulation Details - {formatDate(selectedSimulation.createdAt)}</Typography>
          
          {/* Configuration Section */}
          <Accordion defaultExpanded sx={{ mb: 2 }}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <SettingsIcon color="action" />
                <Typography variant="subtitle1" fontWeight="bold">Configuration</Typography>
              </Box>
            </AccordionSummary>
            <AccordionDetails>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6} md={3}>
                  <Box>
                    <Typography variant="caption" color="text.secondary">Run By</Typography>
                    <Typography variant="body2">
                      {selectedSimulation.userId?.name || selectedSimulation.userId?.email || 'Unknown User'}
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Box>
                    <Typography variant="caption" color="text.secondary">Created At</Typography>
                    <Typography variant="body2">{formatDate(selectedSimulation.createdAt)}</Typography>
                  </Box>
                </Grid>
                {selectedSimulation.configuration && (
                  <>
                    <Grid item xs={12} sm={6} md={3}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <MessageIcon fontSize="small" color="action" />
                        <Box>
                          <Typography variant="caption" color="text.secondary">Messages</Typography>
                          <Typography variant="body2">{selectedSimulation.configuration.messageCount || 0}</Typography>
                        </Box>
                      </Box>
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <TimerIcon fontSize="small" color="action" />
                        <Box>
                          <Typography variant="caption" color="text.secondary">Duration</Typography>
                          <Typography variant="body2">
                            {selectedSimulation.configuration.simulationDuration || 0} minutes
                          </Typography>
                        </Box>
                      </Box>
                    </Grid>
                  </>
                )}
              </Grid>
            </AccordionDetails>
          </Accordion>

          {/* Client Persona Details */}
          {selectedSimulation.clientPersonaDetails && (
            <Accordion sx={{ mb: 2 }}>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <PersonIcon color="action" />
                  <Typography variant="subtitle1" fontWeight="bold">
                    Client Persona: {selectedSimulation.clientPersonaDetails.name}
                  </Typography>
                </Box>
              </AccordionSummary>
              <AccordionDetails>
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <Typography variant="caption" color="text.secondary">Description</Typography>
                    <Typography variant="body2" paragraph>
                      {selectedSimulation.clientPersonaDetails.description}
                    </Typography>
                  </Grid>
                  
                  {selectedSimulation.clientPersonaDetails.openingLine && (
                    <Grid item xs={12}>
                      <Typography variant="caption" color="text.secondary">Opening Line</Typography>
                      <Typography variant="body2" sx={{ fontStyle: 'italic' }}>
                        "{selectedSimulation.clientPersonaDetails.openingLine}"
                      </Typography>
                    </Grid>
                  )}

                  {selectedSimulation.clientPersonaDetails.background && (
                    <Grid item xs={12}>
                      <Typography variant="caption" color="text.secondary">Background</Typography>
                      <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                        {selectedSimulation.clientPersonaDetails.background}
                      </Typography>
                    </Grid>
                  )}

                  {selectedSimulation.clientPersonaDetails.presentingProblems && (
                    <Grid item xs={12}>
                      <Typography variant="caption" color="text.secondary">Presenting Problems</Typography>
                      <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                        {selectedSimulation.clientPersonaDetails.presentingProblems}
                      </Typography>
                    </Grid>
                  )}

                  {selectedSimulation.clientPersonaDetails.sessionBehaviour && (
                    <Grid item xs={12}>
                      <Typography variant="caption" color="text.secondary">Session Behaviour</Typography>
                      <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                        {selectedSimulation.clientPersonaDetails.sessionBehaviour}
                      </Typography>
                    </Grid>
                  )}

                  {selectedSimulation.clientPersonaDetails.therapeuticDynamics && (
                    <Grid item xs={12}>
                      <Typography variant="caption" color="text.secondary">Therapeutic Dynamics</Typography>
                      <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                        {selectedSimulation.clientPersonaDetails.therapeuticDynamics}
                      </Typography>
                    </Grid>
                  )}

                  {selectedSimulation.clientPersonaDetails.demographics && (
                    <Grid item xs={12}>
                      <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
                        Demographics
                      </Typography>
                      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                        {selectedSimulation.clientPersonaDetails.demographics.ageRange && (
                          <Chip 
                            label={`Age: ${selectedSimulation.clientPersonaDetails.demographics.ageRange}`} 
                            size="small" 
                            variant="outlined" 
                          />
                        )}
                        {selectedSimulation.clientPersonaDetails.demographics.gender && (
                          <Chip 
                            label={`Gender: ${selectedSimulation.clientPersonaDetails.demographics.gender}`} 
                            size="small" 
                            variant="outlined" 
                          />
                        )}
                        {selectedSimulation.clientPersonaDetails.demographics.occupation && (
                          <Chip 
                            label={`Occupation: ${selectedSimulation.clientPersonaDetails.demographics.occupation}`} 
                            size="small" 
                            variant="outlined" 
                          />
                        )}
                      </Box>
                    </Grid>
                  )}

                  {selectedSimulation.clientPersonaDetails.conditions && (
                    <Grid item xs={12}>
                      <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
                        Conditions
                      </Typography>
                      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                        {selectedSimulation.clientPersonaDetails.conditions.map((condition, index) => (
                          <Chip 
                            key={index}
                            label={condition} 
                            size="small" 
                            color="primary"
                            variant="outlined"
                          />
                        ))}
                      </Box>
                    </Grid>
                  )}

                  {selectedSimulation.clientPersonaDetails.severity && (
                    <Grid item xs={12}>
                      <Typography variant="caption" color="text.secondary">Severity Level</Typography>
                      <Typography variant="body2">
                        {selectedSimulation.clientPersonaDetails.severity}
                      </Typography>
                    </Grid>
                  )}
                </Grid>
              </AccordionDetails>
            </Accordion>
          )}

          {/* Therapist Persona Details */}
          {selectedSimulation.therapistPersonaDetails && (
            <Accordion sx={{ mb: 2 }}>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <PsychologyIcon color="action" />
                  <Typography variant="subtitle1" fontWeight="bold">
                    Therapist Persona: {selectedSimulation.therapistPersonaDetails.name}
                  </Typography>
                </Box>
              </AccordionSummary>
              <AccordionDetails>
                <Typography variant="body2">
                  {selectedSimulation.therapistPersonaDetails.description}
                </Typography>
              </AccordionDetails>
            </Accordion>
          )}

          <Divider sx={{ my: 2 }} />
          
          {/* Transcript Section */}
          <Typography variant="h6" sx={{ mb: 2 }}>Conversation Transcript</Typography>
          {isLoadingDetails && <CircularProgress size={24} />}
          {!isLoadingDetails && selectedSimulation.transcript && (
            <TranscriptDisplay
              transcript={selectedSimulation.transcript || []}
            />
          )}
          {!isLoadingDetails && !selectedSimulation.transcript && (
            <Typography color="textSecondary">Transcript data is missing for this simulation.</Typography>
          )}

          {/* Review Section */}
          <Box mt={3}>
            <Typography variant="h6">Review</Typography>
            {reviewError && <Typography color="error">{reviewError}</Typography>}
            {isReviewLoading && <CircularProgress size={20} />}
            {!isReviewLoading && review && review.evaluation && (
              <Card variant="outlined" sx={{ mt: 1 }}>
                <CardContent>
                  <Typography variant="subtitle2" gutterBottom>Evaluation:</Typography>
                  <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>{review.evaluation}</Typography>
                  {review.recommendations && (
                    <>
                      <Typography variant="subtitle2" gutterBottom sx={{ mt: 2 }}>Recommendations:</Typography>
                      <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>{review.recommendations}</Typography>
                    </>
                  )}
                  <Typography variant="caption" display="block" sx={{mt:1}}>Reviewed at: {formatDate(review.generatedAt)}</Typography>
                </CardContent>
              </Card>
            )}
            {!isReviewLoading && !review?.evaluation && (
              <Typography color="textSecondary" sx={{mt:1}}>No review available for this simulation.</Typography>
            )}
          </Box>

          {/* Summary Section */}
          <Box mt={3}>
            <Typography variant="h6">Summary</Typography>
            {summaryError && <Typography color="error">{summaryError}</Typography>}
            {isSummaryLoading && <CircularProgress size={20} />}
            {!isSummaryLoading && summary && (summary.therapistView || summary.clientView) && (
              <Card variant="outlined" sx={{ mt: 1 }}>
                <CardContent>
                  {summary.therapistView && (
                    <>
                      <Typography variant="subtitle2" gutterBottom>
                        Therapist View Summary:
                        {summary.therapistView.includes("Unable to generate") && (
                          <Chip 
                            label="Failed" 
                            color="error" 
                            size="small" 
                            sx={{ ml: 1 }} 
                          />
                        )}
                      </Typography>
                      <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', mb: 2 }}>{summary.therapistView}</Typography>
                    </>
                  )}
                  {summary.clientView && (
                    <>
                      <Typography variant="subtitle2" gutterBottom>
                        Client View Summary:
                        {summary.clientView.includes("Unable to generate") && (
                          <Chip 
                            label="Failed" 
                            color="error" 
                            size="small" 
                            sx={{ ml: 1 }} 
                          />
                        )}
                      </Typography>
                      <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>{summary.clientView}</Typography>
                    </>
                  )}
                  <Typography variant="caption" display="block" sx={{mt:1}}>Summarized at: {formatDate(summary.generatedAt)}</Typography>
                </CardContent>
              </Card>
            )}
            {!isSummaryLoading && (!summary || (!summary.therapistView && !summary.clientView)) && (
              <Typography color="textSecondary" sx={{mt:1}}>No summary available for this simulation.</Typography>
            )}
          </Box>
        </Paper>
        </>
      ) : (
        // List View
        <>
          {isLoadingList && <Typography>Loading history...</Typography>}
          {!isLoadingList && simulations.length === 0 && <Typography>No simulation history found.</Typography>}
          {!isLoadingList && simulations.length > 0 && (
            <TableContainer component={Paper} elevation={3}>
              <Table sx={{ minWidth: 650 }} aria-label="simulation history table">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 'bold' }}>Date</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>Client Persona</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>Therapist Persona</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>Run By</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {simulations.map(sim => (
                    <TableRow
                      key={sim._id}
                      hover
                      sx={sim._id === lastViewedSimId ? { backgroundColor: 'action.hover' } : {}}
                      onClick={() => handleSelectSimulation(sim._id)} // Make row clickable
                      style={{ cursor: 'pointer' }}
                    >
                      <TableCell>{formatDate(sim.createdAt)}</TableCell>
                      <TableCell>{sim.clientPersonaKey}</TableCell>
                      <TableCell>{sim.therapistPersonaKey}</TableCell>
                      <TableCell>{sim.userId?.name || sim.userId?.email || 'Unknown User'}</TableCell>
                      <TableCell>
                        <Button
                          variant="outlined"
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation(); // Prevent row click from also firing
                            handleSelectSimulation(sim._id);
                          }}
                          disabled={isLoadingDetails}
                        >
                          View Details
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </>
      )}
      
      {/* Export Modal */}
      {selectedSimulation && (
        <SimulationExportModal
          open={exportModalOpen}
          onClose={() => setExportModalOpen(false)}
          simulationId={selectedSimulation._id}
          token={token}
        />
      )}
    </Box>
  );
};

export default SimulationHistoryViewer;
